import { useQuery } from "@apollo/client"
import { useSSRDisabled } from "@skyltmax/ds9/hooks"
import { type JSONContent } from "@tiptap/core"
import cn from "classnames"
import React, { useEffect, useMemo, useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import StaticTiptapDisplay from "../static_tiptap_display"
import { MinimaticAddButton, MinimaticConfig } from "./minimatic"
import { type MinimaticAlignmentConfiguration } from "./minimatic/alignment"
import { MinimaticColors } from "./minimatic/colors"
import { MinimaticFixmethods } from "./minimatic/fixmethods"
import { MinimaticFonts } from "./minimatic/fonts"
import { MinimaticInputs } from "./minimatic/text"
import SignQuantity from "./sign_quantity"
import { signtagExtensions } from "./tag_content"
import { graphql } from "@/api"
import { type ClientRole, type SignsExampleQuery } from "@/api/graphql"
import { type Fontdata, type Fontmap } from "@/common/client_data"
import AddBundleToCart from "@/common/components/add_bundle_to_cart"
import AddExampleToCart from "@/common/components/add_example_to_cart"
import PreviewCarousel, { isMinimaticSlide } from "@/common/components/preview_carousel"
import { type PreviewSlide } from "@/common/components/preview_modal"
import { useFeatureIsOn } from "@/common/growthbook-react"
import { sizeUnit } from "@/common/locales"
import { isBlank } from "@/common/util"
import Calc from "@/common/util/calculator"
import { setEnabledFlags } from "@/signomatic/app/flags"
import {
  loadMinimatic,
  type MinimaticData,
  type MinimaticSignData,
  type MinimaticSignState,
  useMinimaticLoaded,
} from "@/signomatic/app/minimatic"
import type SignText from "@/signomatic/app/objects/sign_text"
import type Sign from "@/signomatic/app/sign"
import { ChevronDown, EditFill, EditOutline, HeartOutline, TruckOutline, WarningTriangleColor } from "@/skylticons"

const exampleDoc = graphql(`
  query SignsExample($id: ID!, $vatCode: String) {
    example(id: $id) {
      id
      singleTitle
      description
      minimatic
      previewSvgs
      imageAlt
      width
      height
      localPrice(vatCode: $vatCode)
      fixPrice
      addToCartEnabled
      productDescriptionClient
      shoppingEnabled

      minimaticConfiguration {
        id
        backgroundColors
        backgroundColorsEnabled
        contentColors
        contentColorsEnabled
        fixedColors
        fonts
        fontsEnabled
        fixmethods
        fixmethodsEnabled

        textAlign {
          text1
          text2
          text3
        }
      }

      shoppingItem {
        id
        description
      }

      signTag {
        id
        content
        information
      }

      exampleable {
        ... on Bundle {
          id
          name
          itemNumber
          showSvg
          signTypeName
          fixmethodName

          images {
            id
            png
            png2x
            webp
            webp2x
            googleShopping
          }
        }

        ... on Sign {
          id
          defaultDescription
          fixmethodName
          adminOnly
          signType
          metaProduct
          text1
          text2
          text3
          fabricJson
          minimaticAllowed
          dualcolor
          transparent
          multicolor
          contentColor
          fonts
          fixmethod
          backgroundColor
          signTypeNameLocal: signTypeName
          signTypeNameEn

          images {
            id
            png
            png2x
            webp
            webp2x
            googleShopping
          }
        }
      }
    }
  }
`)

const signdataDoc = graphql(`
  query SignsExampleSigndata {
    signomaticData {
      signData
      metaProducts
      fontData

      fixmethodsMedia {
        key

        fixmethods {
          key
          image
          video
          text
        }
      }
    }
  }
`)

const asSign = (exampleable?: SignsExampleQuery["example"]["exampleable"]) => {
  if (exampleable?.__typename === "Sign") {
    return exampleable
  }

  return null
}

const asBundle = (exampleable?: SignsExampleQuery["example"]["exampleable"]) => {
  if (exampleable?.__typename === "Bundle") {
    return exampleable
  }

  return null
}

const calcLeft = (text: SignText, align: string, initAlign: string | undefined) => {
  let left = text.state.left

  if (initAlign == "right") {
    if (align == "center") {
      left = left - text.state.width / 2
    } else if (align == "left") {
      left = left - text.state.width
    }
  } else if (initAlign == "left") {
    if (align == "right") {
      left = left + text.state.width
    } else if (align == "center") {
      left = left + text.state.width / 2
    }
  } else if (initAlign == "center") {
    if (align == "right") {
      left = left + text.state.width / 2
    } else if (align == "left") {
      left = left - text.state.width / 2
    }
  }

  return left
}

export const updateAlignment = (
  minimatic: Sign,
  align1: string | undefined,
  align2: string | undefined,
  align3: string | undefined,
  initAlign1: string | null | undefined,
  initAlign2: string | null | undefined,
  initAlign3: string | null | undefined
) => {
  const [text1, text2, text3] = minimatic.allObjects.filter(obj => typeof obj.state.text === "string") as SignText[]

  if (align1 && initAlign1) {
    const left = calcLeft(text1, align1, initAlign1)

    text1.set("originX", align1)
    minimatic.updateExactObjectState(text1, { justify: align1, left })
  }

  if (align2 && initAlign2) {
    const left = calcLeft(text2, align2, initAlign2)

    text2.set("originX", align2)
    minimatic.updateExactObjectState(text2, { justify: align2, left })
  }

  if (align3 && initAlign3) {
    const left = calcLeft(text3, align3, initAlign3)

    text3.set("originX", align3)
    minimatic.updateExactObjectState(text3, { justify: align3, left })
  }
}

interface ExampleTextProps {
  text?: string | null
  json?: JSONContent | null
}

const ExampleText: React.FC<ExampleTextProps> = ({ text, json }) => {
  const { t } = useTranslation()

  const click = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    document.getElementById("description")?.scrollIntoView({ behavior: "smooth" })
  }

  return (
    <>
      <div className="sign-content mb-2 hidden lg:block">
        {text && <div className="line-clamp-2 h-12" dangerouslySetInnerHTML={{ __html: text }}></div>}
        {json && <StaticTiptapDisplay extensions={signtagExtensions} json={json} className="line-clamp-2 h-12" />}
      </div>

      <button
        className="link-button mb-6 hidden max-w-fit items-center text-base underline lg:flex"
        onClick={click}
        type="button"
      >
        {t("misc.read_more")}
        <ChevronDown className="h-6 w-6" />
      </button>
    </>
  )
}

interface SignsExampleProps {
  exampleId: string
  role: ClientRole
  vatCode?: string
  recommended?: string
  adoptUrl?: string
  signCategoryId?: number
  admin?: boolean
  featureFlags: string[]
}

const SignsExample: React.FC<SignsExampleProps> = ({
  exampleId,
  role,
  vatCode,
  recommended,
  adoptUrl,
  signCategoryId,
  admin,
  featureFlags,
}) => {
  const { t } = useTranslation()
  const result = useQuery(exampleDoc, { variables: { id: exampleId, vatCode } })
  const example = result.data?.example
  const exampleable = example?.exampleable
  const sign = asSign(exampleable)
  const bundle = asBundle(exampleable)

  setEnabledFlags(featureFlags)

  const examplerId = parseInt(exampleId)

  const price = example?.localPrice
  const [count, setCount] = useState(1)

  const [minimatic, setMinimatic] = useState<Sign>()
  const [minimaticFailed, setMinimaticFailed] = useState(false)
  const [text1, setText1] = useState<string>()
  const [text2, setText2] = useState<string>()
  const [text3, setText3] = useState<string>()
  const [textColor, setTextColor] = useState<string>()
  const [color, setColor] = useState<string>()
  const [contentColor, setContentColor] = useState<string>()
  const [font, setFont] = useState<string>()
  const [fixmethod, setFixmethod] = useState<string>()
  const [previewSvg, setPreviewSvg] = useState<string[]>()
  const [signcode, setSigncode] = useState<string>()
  const [initAlignment, setInitAlignment] = useState<MinimaticAlignmentConfiguration | undefined>()
  const loaded = useMinimaticLoaded(minimatic)
  const minimaticFonts = (example?.minimaticConfiguration?.fonts || [])
    .concat(sign?.fonts || [])
    .map(f => f.toLowerCase())

  const dataResult = useQuery(signdataDoc, { skip: !sign || !example?.minimatic, ssr: false })
  const signomaticData = useMemo(() => {
    const data = dataResult.data?.signomaticData

    if (!data) {
      return null
    }

    let fontdataCopy

    if (!admin) {
      const initFontData = data.fontData
      const filemap: Fontmap = {}

      for (const name in initFontData?.filemap) {
        if (minimaticFonts?.includes(name)) {
          filemap[name] = initFontData?.filemap[name]
        }
      }

      fontdataCopy = {
        filemap,
        disabled: initFontData.disabled,
        sets: initFontData.sets,
      }
    }

    return {
      signData: [...data.signData] as SignData[],
      fixmethodsMedia: [...data.fixmethodsMedia],
      fontData: (admin ? { ...data.fontData } : { ...fontdataCopy }) as Fontdata,
      metaProducts: [...data.metaProducts],
    }
  }, [dataResult.data?.signomaticData, minimaticFonts, admin])

  useEffect(() => {
    async function load(sign: MinimaticSignState, data: MinimaticSignData) {
      const minimatic = await loadMinimatic(sign, data)
      setMinimatic(minimatic)
    }

    // Unload minimatic if disabling minimatic
    if (minimatic && !example?.minimatic) {
      setMinimatic(undefined)
      setPreviewSvg(undefined)
      setSigncode(undefined)
    }

    if (!minimatic && sign && example?.minimatic && signomaticData) {
      load(sign, signomaticData).catch(error => {
        console.error(error)
        setMinimaticFailed(true)
      })
    }
  }, [minimatic, sign, signomaticData, example?.minimatic])

  const updateCount = (count: number) => {
    setCount(count)

    if (signcode) {
      setSigncode(JSON.stringify({ ...JSON.parse(signcode), count }))
    }
  }

  useEffect(() => {
    if (minimatic) {
      const handler = async () => {
        const previewSVG = await minimatic.toSVG()
        setPreviewSvg(previewSVG)

        const code = await minimatic.signcode(true)

        if (!code) {
          console.warn("signcode is null")
          return
        }

        code.count = count || 1
        code.sign_category_id = signCategoryId || null
        code.exampler_id = examplerId || null

        setSigncode(JSON.stringify(code))
      }

      minimatic.onStateUpdated.subscribe(handler)

      return () => minimatic.onStateUpdated.unsubscribe(handler)
    }

    return
  }, [minimatic, count, examplerId, signCategoryId])

  const saveConfig = useCallback(
    (align1: string | undefined, align2: string | undefined, align3: string | undefined) => {
      if (minimatic) {
        updateAlignment(
          minimatic,
          align1,
          align2,
          align3,
          initAlignment?.textAlign?.text1,
          initAlignment?.textAlign?.text2,
          initAlignment?.textAlign?.text3
        )

        setInitAlignment({ textAlign: { text1: align1, text2: align2, text3: align3 } })
      }
    },
    [minimatic, initAlignment]
  )

  useEffect(() => {
    if (minimatic && loaded && !initAlignment) {
      const [text1, text2, text3] = minimatic.allObjects.filter(obj => typeof obj.state.text === "string") as SignText[]
      const exampleAlign = example?.minimaticConfiguration?.textAlign

      const align1 = exampleAlign?.text1 || text1?.textAlign
      const align2 = exampleAlign?.text2 || text2?.textAlign
      const align3 = exampleAlign?.text3 || text3?.textAlign

      updateAlignment(minimatic, align1, align2, align3, text1?.originX, text2?.originX, text3?.originX)

      setInitAlignment({ textAlign: { text1: align1, text2: align2, text3: align3 } })
    }
  }, [minimatic, loaded, initAlignment, example])

  const ssrDisabled = useSSRDisabled()
  const minimaticLoading = (!minimatic && !minimaticFailed) || ssrDisabled

  const data: MinimaticData = {
    text1,
    text2,
    text3,
    textColor,
    color,
    contentColor,
    font,
    fixmethod,
  }

  const adoptHref = `${adoptUrl}&count=${count}&minimatic_data=${encodeURIComponent(JSON.stringify(data))}`

  const handleAdopt = (e: React.MouseEvent) => {
    if (minimatic) {
      e.preventDefault()

      const signState = minimatic.state

      // Convert state text objects to center alignment
      if (signState.objects) {
        signState.objects.forEach((obj: any) => {
          if (typeof obj.text === "string") {
            if (obj.justify === "right") {
              obj.left = obj.left - obj.width / 2
            } else if (obj.justify === "left") {
              obj.left = obj.left + obj.width / 2
            }
          }
        })
      }

      minimatic.storeImmediate(signState, true)

      window.location.href = `${adoptHref}&stored=true`
    }
  }

  const widthLocal = sizeUnit === "in" ? Calc.mmToIn(example?.width || 0) : example?.width
  const heightLocal = sizeUnit === "in" ? Calc.mmToIn(example?.height || 0) : example?.height
  const signDimensions = widthLocal && heightLocal ? `${widthLocal}x${heightLocal} ${sizeUnit}` : null
  const fixMethod = sign?.fixmethodName || bundle?.fixmethodName

  const title = sign?.signTypeNameLocal || bundle?.signTypeName
  const subTitle = [signDimensions, fixMethod].filter(e => !!e).join(", ")

  const slides: PreviewSlide[] = []

  const examplesShoppingV2 = useFeatureIsOn("examples-shopping-9292")

  if (example) {
    let images = sign?.images || bundle?.images || []
    const shoppingImage = images.find(img => img.googleShopping)

    const shoppingItem = examplesShoppingV2 ? example.shoppingEnabled : example.shoppingItem !== null

    if (shoppingImage && shoppingItem) {
      slides.push({
        type: "shopping",
        png: shoppingImage?.png,
        png2x: shoppingImage?.png2x,
        webp: shoppingImage?.webp,
        webp2x: shoppingImage?.webp2x,
        title,
        subTitle,
      })
    }

    example?.previewSvgs?.forEach((svg, i) => {
      if (example.minimatic) {
        slides.push({
          type: "minimatic",
          svg: previewSvg && previewSvg[i] ? previewSvg[i] : svg,
          sign: minimatic,
          loadingFailed: minimaticFailed,
          title,
          subTitle,
        })

        return
      }

      slides.push({
        type: "svg",
        svg,
        title,
        subTitle,
      })
    })

    images = shoppingImage && shoppingItem ? images.filter(img => !img.googleShopping) : images

    images.forEach(img => {
      slides.push({
        type: "image",
        png: img.png,
        png2x: img.png2x,
        webp: img.webp,
        webp2x: img.webp2x,
        title,
        subTitle,
      })
    })

    if (example.width <= 1040 && example.height <= 1600 && (bundle?.showSvg || sign)) {
      example?.previewSvgs?.forEach((svg, i) => {
        slides.push(
          {
            type: "comparison",
            comparison: "room",
            svg: previewSvg && previewSvg[i] ? previewSvg[i] : svg,
            width: example.width,
            height: example.height,
          },
          {
            type: "comparison",
            comparison: "human",
            svg: previewSvg && previewSvg[i] ? previewSvg[i] : svg,
            width: example.width,
            height: example.height,
          }
        )
      })
    }
  }

  const [currentSlide, setCurrentSlide] = useState(0)
  const minimaticSlide = slides.find(isMinimaticSlide)
  const minimaticSlideIndex = minimaticSlide && slides.indexOf(minimaticSlide)

  const setMinimaticSlide = () => {
    if (minimaticSlideIndex) {
      setCurrentSlide(minimaticSlideIndex)
    }
  }

  const addBtn = example?.minimatic || example?.addToCartEnabled

  const exampleProductDescription = examplesShoppingV2
    ? example?.productDescriptionClient
    : example?.shoppingItem?.description

  return (
    <>
      <div className="flex flex-col lg:flex-row [&_.canvas-wrapper]:h-full [&_.canvas-wrapper]:!w-full [&_.signv2_svg]:h-full [&_.signv2_svg]:w-full">
        <div className="relative lg:pb-16">
          <PreviewCarousel
            slides={slides}
            alt={example?.imageAlt}
            currentSlide={currentSlide}
            setCurrentSlide={setCurrentSlide}
            className="top-4 lg:sticky"
          />
        </div>

        <div className="sign-info ml-0 mt-4 flex h-fit grow flex-col px-3 lg:ml-12 lg:mt-0 lg:px-0 xl:w-auto">
          <div className="flex flex-row">
            <div className="flex w-full flex-col justify-between">
              {example?.singleTitle && (
                <h1 id="item-page-header" className="heading-four xl:heading-three mb-1 lg:mb-2">
                  {example.singleTitle}
                </h1>
              )}
              <p className="paragraph-sm mb-4 lg:mb-6">{example?.description}</p>
            </div>
          </div>

          {admin && sign && (
            <MinimaticConfig
              exampleId={exampleId}
              enabled={example?.minimatic}
              allowed={sign.minimaticAllowed}
              sign={sign}
              signomaticData={signomaticData}
              configuration={example?.minimaticConfiguration || { textAlign: initAlignment?.textAlign }}
              onSave={saveConfig}
              className="mb-4"
            />
          )}

          {!example?.minimatic && (
            <>
              {(!isBlank(exampleProductDescription) || (!example?.signTag.content && example?.signTag.information)) && (
                <ExampleText text={exampleProductDescription || example?.signTag.information} />
              )}
              {isBlank(exampleProductDescription) && example?.signTag.content && (
                <ExampleText json={example.signTag.content} />
              )}
            </>
          )}

          {sign && example?.minimatic && (
            <>
              <MinimaticInputs
                minimatic={minimatic}
                loadingFailed={minimaticFailed}
                text1={text1}
                text2={text2}
                text3={text3}
                defaultText1={sign.text1}
                defaultText2={sign.text2}
                defaultText3={sign.text3}
                signType={sign.signType}
                setText1={setText1}
                setText2={setText2}
                setText3={setText3}
              />

              <MinimaticFonts
                minimatic={minimatic}
                config={example.minimaticConfiguration}
                sign={sign}
                loadingFailed={minimaticFailed}
                setFont={setFont}
                adoptHref={adoptHref}
              />

              <MinimaticColors
                minimatic={minimatic}
                config={example.minimaticConfiguration}
                sign={sign}
                loadingFailed={minimaticFailed}
                setTextColor={setTextColor}
                setColor={setColor}
                setContentColor={setContentColor}
                setMinimaticSlide={setMinimaticSlide}
              />

              <MinimaticFixmethods
                minimatic={minimatic}
                config={example.minimaticConfiguration}
                loadingFailed={minimaticFailed}
                setFixmethod={setFixmethod}
                fixmethodsMedia={signomaticData?.fixmethodsMedia}
                setMinimaticSlide={setMinimaticSlide}
              />

              {minimaticFailed && (
                <div className="mb-4 flex items-center rounded border-1 border-warning bg-warning-bg p-3">
                  <WarningTriangleColor className="h-6 w-6 min-w-[24px] self-baseline [&_.primary]:fill-warning [&_.primary]:text-warning [&_.secondary]:fill-ink-100 [&_.secondary]:text-ink-100" />
                  <span className="ml-2 text-base">{t("examples.preview_failed_add_cart")}</span>
                </div>
              )}
            </>
          )}

          {sign && (
            <SignQuantity
              examplerId={examplerId}
              id={sign.id}
              clientRole={role}
              price={price}
              updateCount={updateCount}
              minimatic={example?.minimatic}
              fixedPrice={example?.fixPrice}
              signcode={signcode}
            />
          )}

          {bundle && (
            <>
              <div className="heading-four mb-0 mt-6 flex gap-1">
                <span className="total-amount">{price}</span>
              </div>
              <p className="mb-4 text-xs leading-normal">
                {t("article.price")} {role === "business" ? t("order.exc_vat") : t("order.inc_vat")}
              </p>
            </>
          )}

          <div className="single-buttons mb-4 flex flex-col gap-3 lg:max-w-[512px] xl:flex-row">
            {sign && (
              <>
                {example?.minimatic && (
                  <MinimaticAddButton
                    signId={sign.id}
                    minimatic={minimatic}
                    disabled={minimaticLoading}
                    count={count}
                    examplerId={examplerId}
                    signCategoryId={signCategoryId}
                  />
                )}

                {!example?.minimatic && addBtn && (
                  <AddExampleToCart
                    signId={sign.id}
                    recommended={recommended}
                    minimaticTextOne={text1}
                    minimaticTextTwo={text2}
                    minimaticTextThree={text3}
                    count={count}
                    examplerId={examplerId}
                    signCategoryId={signCategoryId}
                  />
                )}

                {(admin || !sign.adminOnly) && !example?.fixPrice && (
                  <a
                    onClick={handleAdopt}
                    href={adoptHref}
                    className={cn("example-to-signomatic button button-large adopt-btn mb-0 ml-0 flex xl:flex-1", {
                      "button-secondary": addBtn,
                      "button-emphasis": !addBtn,
                    })}
                  >
                    {addBtn ? <EditOutline /> : <EditFill />}
                    {t("sign.redesign_it")}
                  </a>
                )}
              </>
            )}

            {bundle && addBtn && (
              <AddBundleToCart
                bundleId={bundle.id}
                recommended={recommended}
                signCategoryId={signCategoryId}
                examplerId={examplerId}
              />
            )}
          </div>

          <div className="pb-12 pt-2 lg:border-none lg:pb-16">
            <div className="paragraph-xs mb-4 flex items-center">
              <HeartOutline className="mr-3 h-6 w-6" />
              {t("start.guarantee_point")}
            </div>
            <div className="paragraph-xs mb-0 flex items-center">
              <TruckOutline className="mr-3 h-6 w-6" />
              {t("start.intro_point_2")}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SignsExample
